import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { signIn, completeNewPassword, forgotPassword, confirmForgotPassword } from '../lib/cognitoutils';
import Logo from '../img/logo-square.png'; // Import the SVG file
import { UserContext } from '../lib/usercontext';
import { useLocation } from 'react-router-dom'; // Ensure useLocation is imported

const LoginForm = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const [name, setName] = useState(''); // State for the name
  const [phoneNumber, setPhoneNumber] = useState(''); // State for the phone number
  const [phoneNumberAlreadySet, setPhoneNumberAlreadySet] = useState(true); // Track if phone was already set
  const [confirmNewPassword, setConfirmNewPassword] = useState(''); // State for confirming the new password
  const [newPassword, setNewPassword] = useState(''); // State for the new password
  const [newPasswordRequired, setNewPasswordRequired] = useState(false); // State to track if new password is required
  const [passwordResetRequired, setPasswordResetRequired] = useState(false); // State to track if password reset is required
  const [verificationCode, setVerificationCode] = useState(''); // State for the verification code
  const [errorMessage, setErrorMessage] = useState(''); // New state for error message

  const { setUser } = useContext(UserContext);

  const [cognitoUser, setCognitoUser] = useState(null);
  const [userSession, setUserSession] = useState(null);

  const location = useLocation(); // Use location to access query params

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('sessionExpired') === 'true') {
      setErrorMessage('Session expired, please log in again.');
    }
  }, [location]);

  const handleNewPasswordSubmit = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('New passwords do not match.');
      return;
    }

    try {
      const additionalAttributes = {};

      // Only include name if it's provided and needs to be set
      if (name) {
        additionalAttributes['name'] = name;
      }

      // Only include phone_number if it wasn't already set during user creation
      // Cognito will throw an error if you try to modify an already provided attribute
      if (!phoneNumberAlreadySet && phoneNumber) {
        const formattedPhoneNumber = `+1${phoneNumber.replace(/-/g, '')}`;
        additionalAttributes['phone_number'] = formattedPhoneNumber;
      }

      const newSession = await completeNewPassword(cognitoUser, newPassword, userSession, additionalAttributes);
      processLoginSuccess(newSession);
    } catch (error) {
      console.error('New password set failed:', error);
      setErrorMessage(error.message || 'New password set failed');

      if (error.name === 'NotAuthorizedException') {
        window.location.href = '/login?error=sessionexpired';
      }
    }
  };

  const handlePasswordResetSubmit = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('New passwords do not match.');
      return;
    }
    try {
      await confirmForgotPassword(cognitoUser.username, verificationCode, newPassword);
      setPasswordResetRequired(false);
      setErrorMessage('Password reset successful. Please sign in.');
    } catch (error) {
      setErrorMessage(error.message || 'Password reset failed');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const sessionData = await signIn(username, password);

      if (sessionData.newPasswordRequired) {
        setNewPasswordRequired(true);
        setCognitoUser(sessionData.user);
        setUserSession(sessionData.session);

        // Check if phone_number is already set in the user's attributes
        const userAttributes = sessionData.userAttributes || {};
        const hasPhoneNumber = userAttributes.phone_number && userAttributes.phone_number.trim() !== '';
        setPhoneNumberAlreadySet(hasPhoneNumber);

        console.log('User attributes from new password challenge:', userAttributes);
        console.log('Phone number already set:', hasPhoneNumber);
      } else {
        processLoginSuccess(sessionData);
      }
    } catch (error) {
      if (error.code === 'PasswordResetRequiredException') {
        await forgotPassword(username);
        setPasswordResetRequired(true);
        setCognitoUser({ username });
        setErrorMessage('Password reset required. Verification code sent to your email.');
      } else {
        console.error('Login failed:', error);
        setErrorMessage(error.message || 'Login failed');
      }
    }
  };

  const processLoginSuccess = (session) => {
    const { accessToken, idToken, refreshToken } = session;
    const payload = idToken.payload;

    setUser({
      userGroups: accessToken.payload['cognito:groups'],
      accessToken: accessToken.jwtToken,
      idToken: idToken,
      refreshToken: refreshToken,
      email: payload.email,
      name: payload.name,
      phoneNumber: payload.phone_number,
      username: payload['cognito:username'],
      emailVerified: payload.email_verified,
      phoneNumberVerified: payload.phone_number_verified,
      authTime: payload.auth_time,
      eventId: payload.event_id,
      issuer: payload.iss,
      audience: payload.aud,
      issuedAt: payload.iat,
      expiration: payload.exp,
      jwtId: payload.jti,
      originJwtId: payload.origin_jti
    });

    if (onLoginSuccess) {
      onLoginSuccess();
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handlePhoneNumberChange = (e) => {
    const input = e.target.value.replace(/\D/g, '');
    const formattedNumber = input.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3').substring(0, 12);
    setPhoneNumber(formattedNumber);
  };

  return (
    <div className="App">
      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img src={Logo} alt="Logo" className="mx-auto h-20 w-auto" />
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md border border-grey p-8 shadow">
          {errorMessage && (
            <div className="mb-4 text-sm font-medium text-red-600">
              {errorMessage}
            </div>
          )}

          {passwordResetRequired ? (
            <form onSubmit={handlePasswordResetSubmit} className="space-y-6">
              <div className="mb-4 text-sm font-medium text-blue-600">
                A password reset is required. Please enter the verification code you received and set a new password.
              </div>
              <div>
                <label htmlFor="verification-code" className="block text-sm font-medium leading-6 text-gray-900">Verification Code</label>
                <div className="mt-2">
                  <input
                    id="verification-code"
                    name="verification-code"
                    type="text"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium leading-6 text-gray-900">New Password</label>
                <div className="mt-2">
                  <input
                    id="new-password"
                    name="new-password"
                    type="password"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="confirm-new-password" className="block text-sm font-medium leading-6 text-gray-900">Confirm New Password</label>
                <div className="mt-2">
                  <input
                    id="confirm-new-password"
                    name="confirm-new-password"
                    type="password"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <button type="submit"
                  className="flex w-full justify-center rounded-md bg-custom-blue px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-custom-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Reset Password
                </button>
              </div>
            </form>
          ) : newPasswordRequired ? (
            <form onSubmit={handleNewPasswordSubmit} className="space-y-6">
              <div className="mb-4 text-sm font-medium text-blue-600">
                Your account requires completion. Please enter your name{!phoneNumberAlreadySet ? ', phone number,' : ''} and set a new password.
              </div>
              <div>
                <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Name</label>
                <div className="mt-2">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>
              {!phoneNumberAlreadySet && (
                <div>
                  <label htmlFor="phone-number" className="block text-sm font-medium leading-6 text-gray-900">Phone Number</label>
                  <div className="mt-2">
                    <input
                      id="phone-number"
                      name="phone-number"
                      type="tel"
                      required
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                    />
                  </div>
                </div>
              )}
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium leading-6 text-gray-900">New Password</label>
                <div className="mt-2">
                  <input
                    id="new-password"
                    name="new-password"
                    type="password"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="confirm-new-password" className="block text-sm font-medium leading-6 text-gray-900">Confirm New Password</label>
                <div className="mt-2">
                  <input
                    id="confirm-new-password"
                    name="confirm-new-password"
                    type="password"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <button type="submit"
                  className="flex w-full justify-center rounded-md bg-custom-blue px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-custom-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Complete Account Setup
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">Username</label>
                <div className="mt-2">
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">Password</label>
                </div>
                <div className="mt-2">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <button type="submit"
                  className="flex w-full justify-center rounded-md bg-custom-blue px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-custom-blue-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  Sign in
                </button>
              </div>

              <div className="text-sm text-center text-gray-500" style={{ fontSize: '12px' }}>
                Version 0.9.95
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  onLoginSuccess: PropTypes.func,
};

export default LoginForm;
